import { useQuery } from '@tanstack/react-query';
import { ethers } from 'ethers';

import { queryOptions } from '~components/hooks/_constants';
import { checkValidContractAddress } from '~config/protocol';
import { EContractName } from '~config/protocol/contractConfig';
import { EVestaAsset } from '~config/types';
import { queryClient } from '~pages/_app';

import { useContractGMXStakingBonus, useContractVaultRewards } from '../../contracts';
import { useSession } from '../../useSession';
import { useContractAddress } from '../useContractAddress';

const QUERY_KEY = 'gmx-boosted-apr';

const GMX_REWARD_DEPOSIT_TOKEN_ADDRESS = '0x35247165119B69A40edD5304969560D0ef486921';

export const useGMXBonusAPR = () => {
  const { address, network } = useSession();
  const { read: gmxStakingBonusRead, address: gmxStakingBonusAddress } = useContractGMXStakingBonus();
  const { read: vaultRewardsRead, address: vaultRewardsAddress } = useContractVaultRewards(EVestaAsset.GMX);
  const gmxStakerAddress = useContractAddress(EContractName.VestaGMXStaking);
  const gmxFeeTrackerAddress = useContractAddress(EContractName.GMXFeeTracker);

  const invalidate = () => queryClient.refetchQueries([QUERY_KEY, network.networkId, address]);

  const res = useQuery(
    [QUERY_KEY, network.networkId, address],
    async () => {
      const [stakedMP, stakedGMX] = await Promise.all([
        gmxStakingBonusRead.getDepositBalances([
          gmxStakerAddress,
          [GMX_REWARD_DEPOSIT_TOKEN_ADDRESS],
          [gmxFeeTrackerAddress],
        ]),
        vaultRewardsRead.totalStaked(),
      ]);
      return {
        stakedMP: ethers.utils.formatEther(stakedMP[0]),
        stakedGMX: ethers.utils.formatEther(stakedGMX),
      };
    },
    queryOptions(
      false,
      checkValidContractAddress([
        gmxStakingBonusAddress,
        vaultRewardsAddress,
        gmxStakerAddress,
        gmxFeeTrackerAddress,
      ]),
    ),
  );

  const boostedApr = Number(res.data?.stakedMP ?? 0) / Number(res.data?.stakedGMX ?? 0);

  return { ...res, data: boostedApr, invalidate };
};
